import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../lib/formatters.js';

interface Check {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

interface VerificationSummaryProps {
  verdict: string;
  confidence: number;
  reasoning: string;
  recommendedAmount?: number | null;
  isoCurrencyCode: string;
  checks: Check[];
  decisionAutoApplied?: boolean;
  finalStatus?: string;
}

const OUTCOME_COLORS: Record<string, string> = {
  PASS: 'text-success bg-success/10',
  WARN: 'text-copper bg-copper-soft',
  FAIL: 'text-danger bg-danger/10',
  INDETERMINATE: 'text-slate-500 bg-slate-100',
};

const VERDICT_COLORS: Record<string, string> = {
  AUTO_APPROVABLE: 'text-success border-success/30 bg-success/5',
  NEEDS_REVIEW: 'text-copper border-copper/30 bg-copper-soft',
  INSUFFICIENT_EVIDENCE: 'text-slate-500 border-slate-200 bg-slate-50',
  LIKELY_REJECT: 'text-danger border-danger/30 bg-danger/5',
};

export function VerificationSummary({
  verdict,
  confidence,
  reasoning,
  recommendedAmount,
  isoCurrencyCode,
  checks,
  decisionAutoApplied,
  finalStatus,
}: VerificationSummaryProps) {
  const { t } = useTranslation();
  const pct = Math.round(confidence * 100);

  return (
    <div className="space-y-4">
      {/* Final decision banner */}
      {finalStatus && (
        <div className={['p-4 border rounded', decisionAutoApplied ? 'border-success/30 bg-success/5' : 'border-copper/30 bg-copper-soft'].join(' ')}>
          <p className="font-semibold text-sm">
            {decisionAutoApplied ? `✓ ${t('wizard.labels.autoApproved')}` : `⏳ ${t('wizard.labels.awaitingReview')}`}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">{t(`wizard.status.${finalStatus}`, { defaultValue: finalStatus })}</p>
        </div>
      )}

      {/* AI Verdict */}
      <div className={['p-4 border rounded', VERDICT_COLORS[verdict] ?? VERDICT_COLORS['NEEDS_REVIEW']].join(' ')}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold uppercase tracking-wide">{t('wizard.labels.finalVerdict')}</span>
          <span className="font-mono text-xs">{t('wizard.labels.overallConfidence')}: <strong>{pct}%</strong></span>
        </div>
        <p className="font-semibold">{t(`wizard.verdicts.${verdict}`, { defaultValue: verdict })}</p>
        <p className="text-sm mt-1 opacity-80">{reasoning}</p>
        {recommendedAmount != null && (
          <p className="text-sm mt-1">
            {t('wizard.labels.recommendedAmount')}: <strong className="font-mono">{formatCurrency(recommendedAmount, isoCurrencyCode)}</strong>
          </p>
        )}
      </div>

      {/* Confidence bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{t('wizard.labels.overallConfidence')}</span>
          <span className="font-mono">{pct}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
          <div
            className={['h-full rounded-sm', pct >= 85 ? 'bg-success' : pct >= 60 ? 'bg-copper' : 'bg-danger'].join(' ')}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Verification checks */}
      {checks.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{t('wizard.labels.verificationChecks')}</p>
          <div className="space-y-1.5">
            {checks.map((c, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2 border border-hairline rounded bg-bone">
                <span className={['text-xs font-bold px-1.5 py-0.5 rounded-sm mt-0.5', OUTCOME_COLORS[c.outcome] ?? OUTCOME_COLORS['INDETERMINATE']].join(' ')}>
                  {t(`wizard.findings.outcomes.${c.outcome}`, { defaultValue: c.outcome })}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-ink">{t(`wizard.findings.checkTypes.${c.checkType}`, { defaultValue: c.checkType })}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{c.reasoning}</p>
                </div>
                <span className="text-xs font-mono text-slate-400 shrink-0">{Math.round(c.confidence * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
